import { createHelpers, createResource, ResourceField } from '@scalingworks/refine-react-admin';
import { FiDollarSign } from 'react-icons/fi';
import { ResourceContext } from '../type';
import { resourceNames } from '../resource-names';
import { Tag, TagProps } from '@scalingworks/react-admin-ui';
import { useTranslate } from '@refinedev/core';

// Define the Wallet type (this would normally come from your API)
interface Wallet {
  id: string;
  createdAt: string;
  updatedAt: string;
  balance: number;
  currency: string;
  customerId: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  lastTransactionDate?: string;
}

enum WalletStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Suspended = 'SUSPENDED',
}

export const WalletStatusTagColor: Record<WalletStatus, TagProps['color']> = {
  [WalletStatus.Active]: 'success',
  [WalletStatus.Inactive]: 'default',
  [WalletStatus.Suspended]: 'error',
};

const { defineFields, defineShowPage } = createHelpers<Wallet>({
  resourceName: resourceNames.wallet,
});

export const walletFields: ResourceField<Wallet>[] = [
  'id',
  'createdAt',
  'updatedAt',
  'balance',
  'currency',
  'customerId',
  'status',
  'lastTransactionDate',
];

export const walletResource = (context: ResourceContext) => {
  return createResource({
    name: resourceNames.wallet,
    label: 'Wallets',
    icon: <FiDollarSign />,
    fields: defineFields(walletFields),
    defaultValues: {
      balance: 0,
      currency: 'USD',
      status: 'ACTIVE',
    } as Partial<Wallet>,
    defaultPageSize: 25,
    defaultSorter: [{ field: 'createdAt', order: 'desc' }],
    allowSearch: true,
    searchConfig: {
      placeholder: ({ t }) =>
        t('wallet.placeholder.search', {
          fallback: 'Search by Customer ID or Wallet ID',
          ns: 'common',
        }),
    },
    createConfig: {
      title: ({ t }) =>
        t('wallet.create.add', {
          fallback: 'Create Wallet',
          ns: 'common',
        }),
    },
    columns: ({ LinkToDetails, navigateToEdit, invokeDelete, t }) => [
      {
        id: 'id',
        header: t('wallet.column.walletId', { fallback: 'Wallet ID', ns: 'common' }),
        accessorKey: 'id',
        cell: (data) => {
          const { id } = data.row.original;
          return <LinkToDetails resourceId={id}>{id}</LinkToDetails>;
        },
      },
      {
        id: 'customerId',
        header: t('wallet.column.customerId', { fallback: 'Customer ID', ns: 'common' }),
        accessorKey: 'customerId',
        cell: (data) => {
          const { id, customerId } = data.row.original;
          return <LinkToDetails resourceId={id}>{customerId}</LinkToDetails>;
        },
      },
      {
        id: 'balance',
        header: t('wallet.column.balance', { fallback: 'Balance', ns: 'common' }),
        accessorKey: 'balance',
        cell: (data) => {
          const { id, balance, currency } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              {currency} {balance.toFixed(2)}
            </LinkToDetails>
          );
        },
      },
      {
        id: 'status',
        header: t('wallet.column.status', { fallback: 'Status', ns: 'common' }),
        accessorKey: 'status',
        cell: (data) => {
          const { id, status } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              <Tag color={WalletStatusTagColor[status as WalletStatus]}>
                {t(`wallet.status.${status?.toLowerCase()}`, {
                  fallback: status,
                  ns: 'common',
                }).toUpperCase()}
              </Tag>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'lastTransactionDate',
        header: t('wallet.column.lastTransaction', { fallback: 'Last Transaction', ns: 'common' }),
        accessorKey: 'lastTransactionDate',
        cell: (data) => {
          const { id, lastTransactionDate } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              {lastTransactionDate ? new Date(lastTransactionDate).toLocaleDateString() : '-'}
            </LinkToDetails>
          );
        },
      },
      {
        id: 'actions',
        header: () => <div />,
        accessorKey: 'id',
        enableSorting: false,
        cell: (data) => {
          const walletId = data.cell.getValue<string>();
          const t = useTranslate();
          
          return (
            <div className="flex gap-2">
              <button
                onClick={() => navigateToEdit({ id: walletId })}
                className="text-primary-500 hover:text-primary-600"
              >
                {t('actions.edit')}
              </button>
            </div>
          );
        },
      },
    ],
    list: {
      render: () => <span>TODO: show the wallet configurations etc</span>
    },
  });
};